import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  InteractionStatus,
  InteractionRequiredAuthError,
  AuthenticationResult
} from '@azure/msal-browser';
import { IMsalContext, useMsal } from '@azure/msal-react';
import { getUser } from 'state/modules/user/thunks';
import LoadingScreen from 'components/@common/LoadingScreen/LoadingScreen';
import AppInit from './AppInit';
import { getLoginRequest } from '../../config/auth';
import AuthenticatedRouter from './AuthenticatedRouter';
import Api from '../../api';
import { hasAuthCookies } from '../../util/auth-cookie-util';
import { DEVICE_COMPATIBILITY } from './util';

export const getAccessToken = async (
  azureContext: IMsalContext
): Promise<AuthenticationResult | null> => {
  const { instance, inProgress } = azureContext;
  const accounts = await instance.getAllAccounts();
  const account = accounts?.[0];
  const loginRequest = getLoginRequest(account);

  if (account && inProgress === InteractionStatus.None) {
    try {
      const response = await instance.acquireTokenSilent(loginRequest);
      return response;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.loginRedirect(loginRequest);
      } else {
        throw error;
      }
    }
  }

  return null;
};

const GatedApp: React.FC = () => {
  const navigate = useNavigate();
  const azureContext = useMsal();
  const dispatch = useDispatch() as any;
  const user = useSelector(({ user }: any) => user, shallowEqual);
  const usingAuthCookies = hasAuthCookies();
  const [isAuthContextAndUserDispatchComplete, setIsAuthContextAndUserDispatchComplete] =
    useState(false);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const handleWindowResize = () => {
    const isDeviceCompatible = window.innerWidth >= DEVICE_COMPATIBILITY;
    if (!isDeviceCompatible) {
      navigate('/device');
      return;
    }

    if (window.location.href.includes('device')) navigate(-1);
  };

  //this useEffect handles redirecting to IDTT-2025 if it detects a 2025 license
  useEffect(() => {
    if (user?.licenses?.find(x => x.productId === 31)) {
      window.location.replace(process.env.REACT_APP_AUTH_2025_REDIRECT_URI!);
    }
  }, [user?.licenses?.length]);

  useEffect(() => {
    if (!isAuthContextAndUserDispatchComplete) {
      const authorizeUser = async () => {
        const result = await getAccessToken(azureContext);

        if (result) Api.setAuthContext(result);

        await dispatch(getUser());
        setIsAuthContextAndUserDispatchComplete(true);
      };

      authorizeUser();
    }
  }, [azureContext, isAuthContextAndUserDispatchComplete, usingAuthCookies, dispatch]);

  if (
    (!usingAuthCookies && azureContext.inProgress !== InteractionStatus.None) ||
    !isAuthContextAndUserDispatchComplete ||
    !user.isAuthenticated
  ) {
    return <LoadingScreen loading />;
  }

  return (
    <AppInit>
      <AuthenticatedRouter />
    </AppInit>
  );
};

export default GatedApp;
